import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const ThemeContext = createContext();

export const useThemeMode = () => useContext(ThemeContext);

const STORAGE_KEY = 'nexus-super-theme';

const getSystemMode = () =>
    window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

const buildTheme = (mode) =>
    createTheme({
        palette: {
            mode,
            primary: { main: mode === 'light' ? '#004df2' : '#5b8dee' },
            background: {
                default: mode === 'light' ? '#f5f7fa' : '#0d1117',
                paper:   mode === 'light' ? '#ffffff' : '#161b26',
            },
            text: {
                primary:   mode === 'light' ? '#00104a' : '#e2e8f0',
                secondary: mode === 'light' ? '#666666' : '#94a3b8',
            },
            divider: mode === 'light' ? '#e0e0e0' : '#2d3748',
        },
        typography: {
            fontFamily: '"Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        },
        components: {
            MuiAppBar: {
                styleOverrides: {
                    root: { backgroundColor: mode === 'light' ? '#00104a' : '#0a0f1e' },
                },
            },
            MuiPaper:  { styleOverrides: { root: { backgroundImage: 'none' } } },
            MuiTab:    { styleOverrides: { root: { textTransform: 'none' } } },
        },
    });

export const AppThemeProvider = ({ children }) => {
    const [preference, setPreferenceState] = useState(
        () => localStorage.getItem(STORAGE_KEY) || 'system'
    );
    const [systemMode, setSystemMode] = useState(getSystemMode);

    // Track OS-level preference changes
    useEffect(() => {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e) => setSystemMode(e.matches ? 'dark' : 'light');
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    const resolvedMode = preference === 'system' ? systemMode : preference;

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', resolvedMode);
    }, [resolvedMode]);

    const setPreference = (pref) => {
        localStorage.setItem(STORAGE_KEY, pref);
        setPreferenceState(pref);
    };

    const theme = useMemo(() => buildTheme(resolvedMode), [resolvedMode]);

    return (
        <ThemeContext.Provider value={{ preference, setPreference, resolvedMode }}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </ThemeContext.Provider>
    );
};
